import mixpanel from "mixpanel-browser";
import {
  ROLE_CLINICIAN,
  ROLE_FACILITY,
  ROLE_ADMIN,
  SUBADMIN,
  LOGIN_STATUS_LABELS,
} from "../constants/AppConstants";

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;

// The identity fields every event must carry. Kept in localStorage as well as
// in Mixpanel's super properties, so events still get them when a flow tracks
// something before identifyUser has run (signup) or after a reload.
const IDENTITY_STORAGE_KEY = "mp_identity";

// Clinical roles only - ROLE_CLINICIAN is the clinician type list
const readStoredIdentity = () => {
  try {
    const raw = localStorage.getItem(IDENTITY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    return {};
  }
};

class MixpanelService {
  static instance = null;

  static getInstance() {
    if (!MixpanelService.instance) {
      MixpanelService.instance = new MixpanelService();
      MixpanelService.instance.initialize();
    }
    return MixpanelService.instance;
  }

  initialize() {
    if (typeof window === "undefined") return;
    try {
      mixpanel.init(MIXPANEL_TOKEN, {
        debug: false,
        track_pageview: false,
        persistence: "localStorage",
      });

      // Synchronously identify returning user if userId is stored in localStorage
      const savedUserId = localStorage.getItem("userId");
      if (savedUserId) {
        mixpanel.identify(savedUserId);
      }
    } catch (err) {}
  }

  /**
   * Build the identity property bag that goes on every event.
   * Key names match the backend (api/helpers/build-mixpanel-user-props.js) so
   * the same property means the same thing whoever sent the event.
   */
  buildIdentityProps(userId, email, userDetails = {}) {
    const userObj = userDetails || {};
    const props = {};

    if (userId) {
      props.user_id = userId;
    }

    // Email
    const emailValue = email || userObj.email;
    if (emailValue) {
      props.email = emailValue;
    }

    // Real name, falling back to the office name for facilities
    const firstName =
      userObj.firstName ||
      userObj.clinicianId?.firstName ||
      userObj.primaryFirstName ||
      userObj.facilityId?.primaryFirstName ||
      "";
    const lastName =
      userObj.lastName ||
      userObj.clinicianId?.lastName ||
      userObj.primaryLastName ||
      userObj.facilityId?.primaryLastName ||
      "";
    const realName =
      [firstName, lastName].filter(Boolean).join(" ").trim() ||
      userObj.facilityId?.officeName ||
      userObj.officeName ||
      "";
    if (realName) {
      props.real_name = realName;
    }

    // Phone
    const phone =
      userObj.phone ||
      userObj.clinicianId?.phone ||
      userObj.primaryPhone ||
      userObj.facilityId?.primaryPhone ||
      "";
    if (phone) {
      props.phone = phone;
    }

    // Role / user type
    const roles = userObj.roles || userObj.clinicianId?.roles || "";
    if (roles) {
      if (ROLE_CLINICIAN.includes(roles)) {
        props.user_type = "clinician";
        props.clinician_type = roles;
      } else if (ROLE_FACILITY.includes(roles)) {
        props.user_type = "facility";
      } else if (ROLE_ADMIN.includes(roles) || roles === SUBADMIN) {
        props.user_type = "admin";
      } else {
        props.user_type = roles;
      }
    }

    // Ids of the underlying clinician / facility
    const clinicianId = userObj.clinicianId?.id || userObj.clinicianId || "";
    if (clinicianId && typeof clinicianId === "string") {
      props.clinician_id = clinicianId;
    }
    const facilityId = userObj.facilityId?.id || userObj.facilityId || "";
    if (facilityId && typeof facilityId === "string") {
      props.facility_id = facilityId;
    }

    // Home ZIP
    const homeZip =
      userObj.clinicianId?.homeAddressZipCode ||
      userObj.homeAddressZipCode ||
      userObj.facilityId?.addresses?.[0]?.zipCode ||
      userObj.addresses?.[0]?.zipCode ||
      "";
    if (homeZip) {
      props.home_zip = homeZip;
    }

    // State
    const stateVal =
      userObj.clinicianId?.homeAddressState ||
      userObj.homeAddressState ||
      userObj.facilityId?.addresses?.[0]?.state ||
      userObj.addresses?.[0]?.state ||
      "";
    if (stateVal) {
      props.state = stateVal;
    }

    // License state(s)
    const licenseDetails =
      userObj.clinicianId?.licenseDetails || userObj.licenseDetails || [];
    if (Array.isArray(licenseDetails) && licenseDetails.length > 0) {
      const licenseStates = [
        ...new Set(licenseDetails.map((detail) => detail?.state).filter(Boolean)),
      ];
      if (licenseStates.length > 0) {
        props.license_state = licenseStates.join(", ");
      }
    }

    // Account status
    if (userObj.status) {
      props.account_status = userObj.status;
    }

    // Signup date
    if (userObj.createdAt) {
      try {
        props.signup_date = new Date(userObj.createdAt).toISOString();
      } catch (err) {
        props.signup_date = userObj.createdAt;
      }
    }

    return props;
  }

  identifyUser(userId, email, userDetails = {}) {
    if (typeof window === "undefined" || !userId) return;
    mixpanel.identify(userId);

    const identity = this.buildIdentityProps(userId, email, userDetails);

    // Super properties: Mixpanel attaches these to every following event
    mixpanel.register(identity);

    // Remembered separately so track() can still fill them in if the super
    // properties were never set (e.g. an event fired during signup)
    try {
      localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(identity));
      if (userId) localStorage.setItem("userId", userId);
    } catch (err) {}

    // Profile properties. The $-prefixed keys are Mixpanel's reserved ones;
    // the readable ones are what the existing reports are built on.
    const profile = { ...identity };
    delete profile.user_id;

    if (identity.email) {
      profile.$email = identity.email;
    }
    if (identity.real_name) {
      profile.$name = identity.real_name;
      profile["Real name"] = identity.real_name;
    }
    if (identity.phone) {
      profile.$phone = identity.phone;
      profile.Phone = identity.phone;
    }
    if (identity.user_type) {
      profile["Role/user_type"] = identity.user_type;
    }
    if (identity.home_zip) {
      profile["Home zip"] = identity.home_zip;
    }
    if (identity.state) {
      profile.State = identity.state;
    }
    if (identity.license_state) {
      profile["License state"] = identity.license_state;
    }
    if (identity.account_status) {
      profile["Account status"] =
        LOGIN_STATUS_LABELS.get(identity.account_status) ||
        identity.account_status;
    }
    if (identity.signup_date) {
      profile["Signup date"] = identity.signup_date;
    }

    if (Object.keys(profile).length > 0) {
      mixpanel.people.set(profile);
    }
  }

  resetUser() {
    if (typeof window === "undefined") return;
    try {
      localStorage.removeItem(IDENTITY_STORAGE_KEY);
    } catch (err) {}
    mixpanel.reset();
  }

  setUserProfile(userId, properties = {}) {
    if (typeof window === "undefined" || !userId) return;
    mixpanel.people.set(properties);
  }

  setSuperProperties(properties = {}) {
    if (typeof window === "undefined") return;
    mixpanel.register(properties);
  }

  track(eventName, properties = {}) {
    if (typeof window === "undefined") return;

    let enhancedProps = { ...properties };

    // Fill in the identity fields from the last identify. Mixpanel's super
    // properties normally cover this, but an event tracked before identify has
    // run (signup) would otherwise go out without them.
    const identity = readStoredIdentity();
    Object.keys(identity).forEach((key) => {
      if (
        enhancedProps[key] === undefined ||
        enhancedProps[key] === null ||
        enhancedProps[key] === ""
      ) {
        enhancedProps[key] = identity[key];
      }
    });

    try {
      const userRole =
        localStorage.getItem("userRole") || localStorage.getItem("adminRole");
      const clinicianId = localStorage.getItem("clinicianId");
      const facilityId = localStorage.getItem("facilityId");

      if (userRole) {
        if (ROLE_CLINICIAN.includes(userRole)) {
          enhancedProps["user_type"] =
            enhancedProps["user_type"] || "clinician";
          enhancedProps["clinician_type"] =
            enhancedProps["clinician_type"] || userRole;
          if (clinicianId)
            enhancedProps.clinician_id =
              enhancedProps.clinician_id || clinicianId;
        } else if (ROLE_FACILITY.includes(userRole)) {
          enhancedProps["user_type"] = enhancedProps["user_type"] || "facility";
          if (facilityId)
            enhancedProps.facility_id = enhancedProps.facility_id || facilityId;
        } else if (ROLE_ADMIN.includes(userRole) || userRole === SUBADMIN) {
          enhancedProps["user_type"] = enhancedProps["user_type"] || "admin";
          // Admin shouldn't get any user_id sent
        } else {
          enhancedProps["user_type"] = enhancedProps["user_type"] || userRole;
        }
      }
    } catch (err) {
      // Ignore if localStorage is inaccessible
    }

    mixpanel.track(eventName, enhancedProps);
  }

  // -----------------------------
  //   Event-specific helpers
  // -----------------------------

  trackAccountCreated({ user_type }) {
    // Lower-cased so it matches the user_type every other event sends
    this.track("Account created", {
      user_type: user_type ? String(user_type).toLowerCase() : user_type,
    });
  }

  trackLogin({ user_type } = {}) {
    let roleUserType = "";
    if (user_type) {
      if (ROLE_CLINICIAN.includes(user_type)) {
        roleUserType = "clinician";
      } else if (ROLE_FACILITY.includes(user_type)) {
        roleUserType = "facility";
      } else if (ROLE_ADMIN.includes(user_type)) {
        roleUserType = "admin";
      } else {
        roleUserType = user_type;
      }
    }
    this.track("Login", roleUserType ? { "user_type": roleUserType } : {});
  }

  trackPageViewed({ Page_name }) {
    this.track("Page Viewed", { Page_name });
  }

  // Event names below match the mobile apps exactly (Title Case) so web and
  // mobile land on the same Mixpanel event instead of two parallel ones.
  trackShiftListViewed({ user_id, clinician_id, platform, timestamp }) {
    this.track("Shift List Viewed", {
      user_id,
      clinician_id,
      platform,
      timestamp,
    });
  }

  trackJobDetailScreenViewed({
    shift_id,
    user_id,
    clinician_id,
    clinical_experience,
    clinician_type,
    facility_type,
    shift_date,
    hourly_rate,
    view_source,
    platform,
    timestamp,
  }) {
    this.track("Job Detail Screen Viewed", {
      shift_id,
      user_id,
      clinician_id,
      clinical_experience,
      clinician_type,
      facility_type,
      shift_date,
      hourly_rate,
      view_source,
      platform,
      timestamp,
    });
  }

  trackShiftDateSelected({
    user_id,
    clinician_id,
    start_date,
    end_date,
    platform,
    timestamp,
  }) {
    this.track("Shift Date Selected", {
      user_id,
      clinician_id,
      start_date,
      end_date,
      platform,
      timestamp,
    });
  }

  trackShiftApplied({
    shift_id,
    user_id,
    clinician_id,
    platform,
    timestamp,
    shift_date,
    facility_type,
    hourly_rate,
    clinician_type,
    clinical_experience,
    flexible_timing_check,
  }) {
    this.track("Shift Applied", {
      shift_id,
      user_id,
      clinician_id,
      platform,
      timestamp,
      shift_date,
      facility_type,
      hourly_rate,
      clinician_type,
      clinical_experience,
      flexible_timing_check,
    });
  }

  trackRegistrationStarted({ user_id, clinician_id, clinician_type, platform, timestamp }) {
    this.track("Registration_started", {
      user_id,
      clinician_id,
      clinician_type,
      platform,
      timestamp,
    });
  }

  trackRegistrationStepViewed({ user_id, clinician_id, clinician_type, platform, timestamp }) {
    this.track("Registration_step_viewed", {
      user_id,
      clinician_id,
      clinician_type,
      platform,
      timestamp,
    });
  }

  trackRegistrationCompleted({ user_id, clinician_id, clinician_type, platform, timestamp }) {
    this.track("Registration_completed", {
      user_id,
      clinician_id,
      clinician_type,
      platform,
      timestamp,
    });
  }
}

export default MixpanelService.getInstance();
